import { faArrowRightArrowLeft } from "@fortawesome/free-solid-svg-icons/faArrowRightArrowLeft";
import { faPlay } from "@fortawesome/free-solid-svg-icons/faPlay";
import isEmpty from "lodash/isEmpty";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { AtiraSelect } from "../../../../components/AtiraSelect";
import { Button } from "../../../../components/Button";
import { Clickable } from "../../../../components/Clickable";
import { Flex } from "../../../../components/Flex";
import { Spinner } from "../../../../components/Spinner";
import { FontSize, Text } from "../../../../components/Text";
import { Modal } from "../../../../components/modal/Modal";
import { useStepContext } from "../../../../components/stepper/StepperContext";
import { useStepperRegisterValidation } from "../../../../components/stepper/useStepperRegisterValidation";
import { entrySliceSelectors } from "../../../../redux/entry/entry.selector";
import { entryActions } from "../../../../redux/entry/entry.slice";
import { formSliceSelectors } from "../../../../redux/forms/form.selector";
import { formActions } from "../../../../redux/forms/form.slice";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Breakpoints } from "../../../../theme/Breakpoints";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";

const Title = styled(Text)`
  font-size: ${FontSize.l};
  color: ${({ theme }) => theme.main};
  font-weight: bold;
  margin-bottom: ${Spacing.s};
`;

const Wrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xxl};
  margin: 2rem auto;
  padding: ${Spacing.m};

  @media (min-width: ${Breakpoints.PHONE}) {
    width: 60%;
  }
`;

const LoadingContainer = styled(Flex)`
  flex-direction: column;
  border-radius: ${Rounded.lg};
  width: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-color: ${({ theme }) => theme.main};
    opacity: 0.1;
    z-index: -1;
  }
`;

const LoadingTitle = styled(Text)`
  font-weight: bold;
  text-align: center;
  padding: ${Spacing.s} ${Spacing.m};
  background-color: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.white};
`;

type Props = {
  fields: { userField: string; foreignField: string }[];
  setFields: Dispatch<
    SetStateAction<{ userField: string; foreignField: string }[]>
  >;
  formId: string;
  setFormId: Dispatch<SetStateAction<string>>;
};

export const IntegrationFacebookFinalizeStep: React.FC<Props> = ({
  fields,
  setFields,
  formId,
  setFormId,
}) => {
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const dispatch = useAppDispatch();

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();

  const facebookIntegration = useAppSelector(
    integrationSliceSelectors.selectFBIntegration,
  );
  const fbResolveFieldsData = useAppSelector(
    integrationSliceSelectors.selectFBResolveFieldsData,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const forms = useAppSelector(formSliceSelectors.selectForms);
  const userTableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );

  const { currentStep } = useStepContext();

  const canGoNext = useCallback(() => {
    return (
      fields.every(
        ({ userField, foreignField }) =>
          userField?.length && foreignField?.length,
      ) && Boolean(formId)
    );
  }, [fields, formId]);

  useStepperRegisterValidation(currentStep, canGoNext);

  useEffect(() => {
    pollingRef.current = setInterval(() => {
      dispatch(
        integrationActions.startFBWebhookFinalizeFields(
          facebookIntegration?._id!,
        ),
      );
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [userId, dispatch, facebookIntegration?._id]);

  useEffect(() => {
    if (fbResolveFieldsData) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    }
  }, [fbResolveFieldsData]);

  useEffect(() => {
    dispatch(formActions.getForms({ userId }));
    dispatch(entryActions.getUserTableSettings({ userId }));
    dispatch(
      integrationActions.startFBWebhookFinalizeFields(
        facebookIntegration?._id!,
      ),
    );
  }, [dispatch, facebookIntegration?._id, userId]);

  return (
    <Flex flexDirection="column" gap="m">
      <Flex gap="s">
        <Title>
          {t("integration.add-modal.facebook.atiraspace.config.title")}
        </Title>
        <Clickable onClick={() => setHelpModalOpen(true)}>
          <Button margin="none" padding={"s"} icon={faPlay}>
            {t("integration.add-modal.facebook.make.import.subtitle")}
          </Button>
        </Clickable>
      </Flex>
      <Flex flexDirection="column" gap="m" width="80%" alignSelf="flex-start">
        <Text>
          {t("integration.add-modal.facebook.atiraspace.config.description")}
        </Text>
      </Flex>

      {isEmpty(fbResolveFieldsData?.foreignFields) ? (
        <Wrapper>
          <LoadingContainer>
            <LoadingTitle>
              {t("integration.add-modal.facebook.atiraspace.config.awaiting")}
            </LoadingTitle>
            <Flex flexDirection="column" margin="xl">
              <Spinner size="3rem" color="main" />
            </Flex>
          </LoadingContainer>
        </Wrapper>
      ) : (
        <Wrapper>
          <LoadingContainer justifyContent="center">
            <Flex flexDirection="column">
              <LoadingTitle fontSize="l">
                {t(
                  "integration.add-modal.facebook.atiraspace.config.form.title",
                )}
              </LoadingTitle>
              <LoadingTitle align="center">
                {t(
                  "integration.add-modal.facebook.atiraspace.config.form.description",
                )}
              </LoadingTitle>
            </Flex>

            <Flex
              flexDirection="column"
              gap="s"
              alignSelf="center"
              justifyContent="center"
              margin="m"
            >
              <AtiraSelect
                options={forms.map((f) => ({
                  label: f.title,
                  value: f._id,
                }))}
                onChange={(v) => setFormId(v)}
                required
                value={formId || undefined}
                containerStyle={{ width: "10rem", alignSelf: "center" }}
              />
              {forms.find((f) => f._id === formId) ? (
                <Text color="darkerSub" fontSize="m" align="center">
                  {t(
                    "integration.add-modal.facebook.atiraspace.config.form.selected",
                    { form: forms.find((f) => f._id === formId)?.title },
                  )}
                </Text>
              ) : null}
            </Flex>
          </LoadingContainer>

          <LoadingContainer justifyContent="center">
            <Flex flexDirection="column">
              <LoadingTitle fontSize="l">
                {t(
                  "integration.add-modal.facebook.atiraspace.config.map.title",
                )}
              </LoadingTitle>
              <LoadingTitle align="center">
                {t(
                  "integration.add-modal.facebook.atiraspace.config.map.description",
                )}
              </LoadingTitle>
            </Flex>

            <Flex flexDirection="column" gap="m" alignSelf="center" margin="m">
              {fields.map((mapping, index) => (
                <Flex key={index} gap="m" alignItems="center">
                  <Flex flexDirection="column" gap="s">
                    {index === 0 ? (
                      <Text color="darkerSub">
                        {t(
                          "integration.add-modal.facebook.atiraspace.config.map.own.title",
                        )}
                      </Text>
                    ) : null}
                    <AtiraSelect
                      containerStyle={{ width: "10rem" }}
                      options={fbResolveFieldsData?.userFields
                        .filter(
                          (c) =>
                            !fields.some(
                              (m, i) => m.userField === c && i !== index,
                            ),
                        )
                        .map((c) => ({
                          label:
                            userTableSettings?.columns.find(
                              (col) => col.field === c,
                            )?.label || c,
                          value: c,
                        }))}
                      placeholder={t(
                        "integration.add-modal.facebook.atiraspace.config.map.own.dropdown.placeholder",
                      )}
                      onChange={(value) => {
                        setFields((prev) => {
                          const newMappings = [...prev];
                          newMappings[index] = {
                            ...mapping,
                            userField: value,
                          };
                          return newMappings;
                        });
                      }}
                      value={mapping.userField || undefined}
                      allowClear
                      disabled={index === 0}
                    />
                  </Flex>

                  <AtiraIcon icon={faArrowRightArrowLeft} />
                  <Flex flexDirection="column" gap="s">
                    {index === 0 ? (
                      <Text color="darkerSub">
                        {t(
                          "integration.add-modal.facebook.atiraspace.config.map.custom.title",
                        )}
                      </Text>
                    ) : null}
                    <AtiraSelect
                      containerStyle={{ width: "10rem" }}
                      options={fbResolveFieldsData?.foreignFields
                        .filter(
                          (f) =>
                            !fields.some(
                              (m, i) => m.foreignField === f && i !== index,
                            ),
                        )
                        .map((f) => ({
                          label: f,
                          value: f,
                        }))}
                      placeholder={t(
                        "integration.add-modal.facebook.atiraspace.config.map.custom.dropdown.placeholder",
                      )}
                      onChange={(value) => {
                        setFields((prev) => {
                          const newMappings = [...prev];
                          newMappings[index] = {
                            ...mapping,
                            foreignField: value,
                          };
                          return newMappings;
                        });
                      }}
                      value={mapping.foreignField || undefined}
                      allowClear
                    />
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </LoadingContainer>
          <Flex flexDirection="column" alignItems="center" padding="s">
            <Text color="darkerSub" align="center">
              {t("integration.add-modal.facebook.atiraspace.config.final-note")}
            </Text>
          </Flex>
        </Wrapper>
      )}
      <Modal
        open={helpModalOpen}
        width={800}
        destroyOnClose
        onClose={() => setHelpModalOpen(false)}
        title={t(
          "integration.add-modal.facebook.atiraspace.config.helpModal.title",
        )}
      >
        <Flex flexDirection="column" gap="m">
          <Text>
            {t(
              "integration.add-modal.facebook.atiraspace.config.helpModal.description",
            )}
          </Text>

          <div style={{ position: "relative", paddingTop: "56.25%" }}>
            <iframe
              title="Facebook Leads Integration Help"
              data-form-title="facebook integration"
              src="https://iframe.mediadelivery.net/embed/484036/523eff7a-9ff4-4696-80a8-ded058dd370d?autoplay=true&loop=true&muted=true&preload=true&responsive=true"
              loading="lazy"
              style={{
                border: 0,
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%",
                borderRadius: 12,
                boxShadow: "0 2px 16px rgba(0,0,0,0.12)",
                background: "#000",
              }}
              allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
              allowFullScreen
            ></iframe>
          </div>
        </Flex>
      </Modal>
    </Flex>
  );
};
